import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Image as ImageIcon, Plus, Trash2, Clock, CheckCircle2, XCircle, RefreshCw, ShoppingCart, Info, Upload, Eye } from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BANNER_PLACEMENTS = [
    { value: 'home_top', label: 'Inicio — arriba del fold' },
    { value: 'home_mid', label: 'Inicio — sección media' },
    { value: 'category_top', label: 'Categorías — parte superior' },
    { value: 'search_top', label: 'Buscador — sobre resultados' },
    { value: 'sidebar', label: 'Barra lateral' },
];

const DEFAULT_BANNER_PACKAGES = [
    { slots: 1, days: 7, price: 149, label: '1 banner · 7 días' },
    { slots: 1, days: 30, price: 399, label: '1 banner · 30 días', popular: true },
    { slots: 3, days: 30, price: 999, label: '3 banners · 30 días' },
];

function normalizeBannerPackages(raw) {
    if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_BANNER_PACKAGES;
    return raw.map((p, i) => ({
        slots: p.slots ?? 1,
        days: p.days ?? 30,
        price: p.price_mxn ?? p.price ?? 0,
        label: p.label || `${p.slots} banner(s) · ${p.days} días`,
        popular: p.popular ?? (i === 1),
    }));
}

const STATUS_BADGE = {
    pending: <Badge className="bg-amber-100 text-amber-800 border-amber-300"><Clock className="h-3 w-3 mr-1" />Pendiente revisión</Badge>,
    active: <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300"><CheckCircle2 className="h-3 w-3 mr-1" />Activo</Badge>,
    rejected: <Badge className="bg-red-100 text-red-800 border-red-300"><XCircle className="h-3 w-3 mr-1" />Rechazado</Badge>,
    paused: <Badge variant="outline" className="text-muted-foreground">Pausado</Badge>,
    expired: <Badge variant="outline" className="text-muted-foreground">Expirado</Badge>,
};

const EMPTY_FORM = { title: '', image_url: '', link_url: '', placement: 'home_top', notes: '' };

export default function DashboardSellerBanners() {
    const [status, setStatus] = useState(null);
    const [banners, setBanners] = useState([]);
    const [bannerPackages, setBannerPackages] = useState(DEFAULT_BANNER_PACKAGES);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [buyOpen, setBuyOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [buyPkg, setBuyPkg] = useState(null);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: st }, { data: bns }, { data: pricing }] = await Promise.all([
                api.get('/me/seller-banners/status'),
                api.get('/me/seller-banners'),
                api.get('/me/seller-banners/pricing'),
            ]);
            setStatus(st);
            setBanners(Array.isArray(bns) ? bns : []);
            if (pricing) {
                setBannerPackages(normalizeBannerPackages(pricing.packages));
            }
        } catch (e) {
            toast.error('Error al cargar banners');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const uploadImage = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setForm(f => ({ ...f, image_url: data.url }));
            toast.success('Imagen subida');
        } catch (e) { toast.error('Error al subir imagen'); }
        finally { setUploading(false); }
    };

    const doCreate = async () => {
        if (!form.image_url) { toast.error('Sube una imagen para el banner'); return; }
        setBusy(true);
        try {
            await api.post('/me/seller-banners', form);
            toast.success('Banner enviado. El equipo lo revisará antes de publicarlo.');
            setCreateOpen(false);
            setForm(EMPTY_FORM);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setBusy(false); }
    };

    const doDelete = async (id) => {
        if (!window.confirm('¿Eliminar este banner?')) return;
        try {
            await api.delete(`/me/seller-banners/${id}`);
            toast.success('Banner eliminado');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const doBuy = async () => {
        if (!buyPkg) return;
        setBusy(true);
        try {
            await api.post('/me/seller-banners/buy-slots', { slots: buyPkg.slots, days: buyPkg.days, price_mxn: buyPkg.price });
            toast.success(`Paquete comprado: ${buyPkg.label}`);
            setBuyOpen(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setBusy(false); }
    };

    if (loading) return <div className="p-6 text-sm text-muted-foreground">Cargando banners…</div>;

    const slotsRemaining = status?.slots_remaining ?? 0;
    const planEnabled = status?.plan_enabled ?? false;

    return (
        <div className="space-y-6" data-testid="dashboard-seller-banners">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <ImageIcon className="h-6 w-6 text-blue-500" />
                    <h1 className="text-2xl font-semibold">Banners de Vendedor</h1>
                </div>
                <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1" />Actualizar</Button>
            </div>

            {!planEnabled && slotsRemaining === 0 && (
                <Alert className="border-blue-200 bg-blue-50">
                    <Info className="h-4 w-4" />
                    <AlertDescription>Los banners de vendedor están disponibles en planes de pago o comprando un paquete con OkWallet. <a href="/planes" className="underline font-medium">Ver planes</a></AlertDescription>
                </Alert>
            )}

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="overflow-hidden">
                    <div className="h-2 bg-gradient-to-r from-blue-400 to-blue-600" />
                    <CardContent className="p-5">
                        <div className="text-xs uppercase text-muted-foreground tracking-wider">Slots disponibles</div>
                        <div className="text-4xl font-bold text-blue-600 mt-1">{slotsRemaining}</div>
                        <div className="text-xs text-muted-foreground mt-1">Banners que puedes activar</div>
                    </CardContent>
                </Card>
                <Card className="overflow-hidden">
                    <div className="h-2 bg-gradient-to-r from-emerald-400 to-emerald-600" />
                    <CardContent className="p-5">
                        <div className="text-xs uppercase text-muted-foreground tracking-wider">Activos ahora</div>
                        <div className="text-4xl font-bold text-emerald-600 mt-1">{banners.filter(b => b.status === 'active').length}</div>
                    </CardContent>
                </Card>
                <Card className="overflow-hidden">
                    <div className="h-2 bg-gradient-to-r from-amber-400 to-amber-600" />
                    <CardContent className="p-5">
                        <div className="text-xs uppercase text-muted-foreground tracking-wider">Pendientes</div>
                        <div className="text-4xl font-bold text-amber-600 mt-1">{banners.filter(b => b.status === 'pending').length}</div>
                    </CardContent>
                </Card>
            </div>

            {/* Actions */}
            <div className="flex gap-3 flex-wrap">
                <Button onClick={() => setCreateOpen(true)} disabled={slotsRemaining <= 0} data-testid="create-banner-btn">
                    <Plus className="h-4 w-4 mr-2" />Crear banner ({slotsRemaining} slots libres)
                </Button>
                <Button variant="outline" onClick={() => setBuyOpen(true)} data-testid="buy-slots-btn">
                    <ShoppingCart className="h-4 w-4 mr-2" />Comprar slots
                </Button>
            </div>

            {/* Info */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-5">
                    <div className="font-semibold mb-2 flex items-center gap-2"><Eye className="h-4 w-4 text-blue-500" />¿Cómo funcionan los banners?</div>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                        <li>Crea tu imagen de banner (recomendado: 1200×300 px para desktop, 800×400 px para mobile)</li>
                        <li>Elige la posición donde aparecerá en el marketplace</li>
                        <li>El equipo de Okgo lo revisará antes de activarlo (24-48 hrs)</li>
                        <li>Una vez activo, tu banner estará visible para todos los compradores</li>
                    </ul>
                </CardContent>
            </Card>

            {/* Banners list */}
            {banners.length === 0 ? (
                <div className="rounded-2xl border border-dashed py-14 text-center text-sm text-muted-foreground">
                    No has creado ningún banner aún.
                </div>
            ) : (
                <div className="space-y-3">
                    {banners.map(b => (
                        <div key={b.id} className="rounded-xl border bg-card overflow-hidden" data-testid={`banner-${b.id}`}>
                            <div className="flex items-start gap-4 p-4">
                                {b.image_url && (
                                    <img src={b.image_url.startsWith('/api/media/') ? b.image_url : `/api/media/${b.image_url}`} alt={b.title} className="h-20 w-40 object-cover rounded-lg shrink-0 border" onError={e => { e.target.style.display = 'none'; }} />
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium">{b.title}</span>
                                        {STATUS_BADGE[b.status] || <Badge variant="outline">{b.status}</Badge>}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        Posición: {BANNER_PLACEMENTS.find(p => p.value === b.placement)?.label || b.placement}
                                    </div>
                                    {b.admin_note && <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mt-1">Nota: {b.admin_note}</div>}
                                    {b.ends_at && <div className="text-[10px] text-muted-foreground mt-1">Vence: {new Date(b.ends_at).toLocaleDateString('es-MX')}</div>}
                                </div>
                                <div className="flex flex-col gap-2 shrink-0">
                                    {['pending', 'paused', 'rejected'].includes(b.status) && (
                                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => doDelete(b.id)}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent data-testid="create-banner-dialog">
                    <DialogHeader><DialogTitle><ImageIcon className="h-5 w-5 inline mr-2 text-blue-500" />Crear banner</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Título del banner</Label>
                            <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ej: Oferta de temporada 30% OFF" data-testid="banner-title" />
                        </div>
                        <div>
                            <Label>Imagen del banner</Label>
                            <div className="space-y-2 mt-1">
                                {form.image_url && (
                                    <img src={form.image_url.startsWith('/api/') ? form.image_url : `/api/media/${form.image_url}`} alt="preview" className="w-full h-24 object-cover rounded-lg border" onError={e => { e.target.style.display = 'none'; }} />
                                )}
                                <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed p-3 hover:bg-accent transition-colors">
                                    <Upload className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">{uploading ? 'Subiendo…' : 'Subir imagen (1200×300 px recomendado)'}</span>
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} disabled={uploading} data-testid="banner-image-upload" />
                                </label>
                            </div>
                        </div>
                        <div>
                            <Label>Posición</Label>
                            <Select value={form.placement} onValueChange={(v) => setForm(f => ({ ...f, placement: v }))}>
                                <SelectTrigger data-testid="banner-placement"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {BANNER_PLACEMENTS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>URL de destino (al dar clic)</Label>
                            <Input value={form.link_url} onChange={(e) => setForm(f => ({ ...f, link_url: e.target.value }))} placeholder="https://… o ruta interna (/buscar?q=oferta)" data-testid="banner-link-url" />
                        </div>
                        <div className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded p-2">
                            Tu banner será revisado por el equipo de Okgo en 24-48 hrs antes de publicarse. Se usará 1 slot de tu saldo disponible.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancelar</Button>
                        <Button onClick={doCreate} disabled={busy || !form.title || !form.image_url} data-testid="banner-create-confirm">
                            {busy ? 'Enviando…' : 'Enviar para revisión'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Buy slots dialog */}
            <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
                <DialogContent data-testid="buy-slots-dialog">
                    <DialogHeader><DialogTitle><ShoppingCart className="h-5 w-5 inline mr-2" />Comprar slots de banner</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="text-sm text-muted-foreground">Elige un paquete. El pago se descuenta de tu OkWallet.</div>
                        <div className="grid gap-3">
                            {bannerPackages.map(pkg => (
                                <button key={pkg.label} type="button"
                                    onClick={() => setBuyPkg(pkg)}
                                    className={`rounded-xl border-2 p-4 text-left transition-all ${buyPkg?.label === pkg.label ? 'border-blue-500 bg-blue-50' : 'border-border hover:border-blue-300'}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="font-bold">{pkg.label}</div>
                                            <div className="text-xs text-muted-foreground">{pkg.slots} slot(s) por {pkg.days} días</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold text-lg">${fmt(pkg.price)}</div>
                                            <div className="text-[10px] text-muted-foreground">MXN</div>
                                        </div>
                                    </div>
                                    {pkg.popular && <Badge className="mt-2 bg-blue-100 text-blue-800 text-[9px]">Más popular</Badge>}
                                </button>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setBuyOpen(false)}>Cancelar</Button>
                        <Button onClick={doBuy} disabled={busy || !buyPkg}>
                            {busy ? 'Procesando…' : `Comprar $${fmt(buyPkg?.price || 0)}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
